/* =============================================================
   Post-sign-in destinations.

   Sign-in is site-wide: people arrive from React routes, from the
   static Interaction Map page (/interaction-map/), and from the
   main website (prajnainnovates.com). `next` may therefore be an
   app path or an absolute URL on the main site. Anything else is
   dropped in favour of the fallback, so a crafted ?next= can't
   bounce someone to another domain.
   ============================================================ */

import type { NavigateFunction } from 'react-router-dom';

export const SITE_URL = 'https://prajnainnovates.com';
const SITE_ORIGINS = ['https://prajnainnovates.com', 'https://www.prajnainnovates.com'];

/* App paths served as their own HTML page rather than by React Router:
   the Interaction Map, and the share pages of maps members publish. */
const STATIC_PREFIXES = ['/interaction-map', '/m/'];

export function safeNext(raw: string | null | undefined, fallback = '/account'): string {
  if (!raw) return fallback;
  /* Browsers read "\" as "/" and skip tabs and line breaks inside a URL,
     so "/\evil.com" and "/<tab>/evil.com" lead off the site. */
  if (/[\\\u0000-\u001f\u007f]/.test(raw)) return fallback;
  try {
    const relative = raw.startsWith('/') && !raw.startsWith('//');
    const here = window.location.origin;
    const u = relative ? new URL(raw, here) : new URL(raw);
    if (relative ? u.origin !== here : !SITE_ORIGINS.includes(u.origin) || u.protocol !== 'https:' || u.username || u.password) return fallback;
    /* The path, tidied by the URL parser, may hold only plain characters, with no segment that
       starts with a dot. This refuses "/..//evil.com" (tidied to "//evil.com", another site),
       encoded slashes that a server decodes after the browser has checked the path, and Azure's
       built-in sign-in pages (/.auth/...), which can send people on to any site. Every address
       in these sites passes. */
    if (!/^\/[A-Za-z0-9\-._~/]*$/.test(u.pathname) || u.pathname.startsWith('//') || u.pathname.includes('/.')) return fallback;
    return relative ? u.pathname + u.search + u.hash : u.href;
  } catch {
    return fallback; // not a URL
  }
}

/** True when the destination is not a React route (full page load needed). */
export function isExternal(target: string): boolean {
  return /^https?:\/\//.test(target) || STATIC_PREFIXES.some((p) => target.startsWith(p));
}

/** Go to a post-sign-in destination, leaving the SPA when it has to. */
export function goTo(navigate: NavigateFunction, target: string): void {
  if (isExternal(target)) window.location.replace(target);
  else navigate(target, { replace: true });
}

/** Link to the account page that brings the visitor back here afterwards. */
export function accountHref(here: string): string {
  return here && here !== '/account' && !here.startsWith('/account?')
    ? `/account?next=${encodeURIComponent(here)}`
    : '/account';
}
